import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import i18n from '../../i18n/i18n';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const getToken = () => {
  const storedUser = sessionStorage.getItem("loggedUser");
  if (!storedUser) return null;
  try {
    const parsed = JSON.parse(storedUser);
    // البحث عن التوكن في أماكن مختلفة
    if (parsed.token) return parsed.token;
    if (parsed.data?.token) return parsed.data.token;
    if (parsed.result?.token) return parsed.result.token;
    if (parsed.user?.token) return parsed.user.token;
    return null;
  } catch (error) {
    return null;
  }
};

// Helper function to get current language
const getCurrentLanguage = () => {
  return i18n.language || 'ar';
};

// Async thunks
export const getClassRooms = createAsyncThunk(
  'classRooms/getClassRooms',
  async (page = 1, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await axios.get(`${BASE_URL}/class_rooms?page=${page}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
          "lang": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'حدث خطأ في جلب بيانات الفصول');
    }
  }
);

export const addClassRoom = createAsyncThunk(
  'classRooms/addClassRoom',
  async (classRoomData, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await axios.post(`${BASE_URL}/class_rooms`, classRoomData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
          "lang": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'حدث خطأ في إضافة الفصل');
    }
  }
);

export const updateClassRoom = createAsyncThunk(
  'classRooms/updateClassRoom',
  async ({ id, classRoomData }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await axios.put(`${BASE_URL}/class_rooms/${id}`, classRoomData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
          "lang": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'حدث خطأ في تحديث الفصل');
    }
  }
);

export const deleteClassRoom = createAsyncThunk(
  'classRooms/deleteClassRoom',
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await axios.delete(`${BASE_URL}/class_rooms/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
          "lang": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'حدث خطأ في حذف الفصل');
    }
  }
);

const initialState = {
  classRooms: [],
  pagination: null,
  loading: false,
  error: null,
  success: null,
};

const classRoomsSlice = createSlice({
  name: 'classRooms',
  initialState,
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Class Rooms
      .addCase(getClassRooms.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getClassRooms.fulfilled, (state, action) => {
        state.loading = false;
        state.classRooms = action.payload.data;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(getClassRooms.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Add Class Room
      .addCase(addClassRoom.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addClassRoom.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.message || 'تم إضافة الفصل بنجاح';
        state.error = null;
      })
      .addCase(addClassRoom.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Class Room
      .addCase(updateClassRoom.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateClassRoom.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.message || 'تم تحديث الفصل بنجاح';
        state.error = null;
      })
      .addCase(updateClassRoom.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete Class Room
      .addCase(deleteClassRoom.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteClassRoom.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.message || 'تم حذف الفصل بنجاح';
        state.error = null;
      })
      .addCase(deleteClassRoom.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearState } = classRoomsSlice.actions;
export default classRoomsSlice.reducer;
